import '@mondough/monzo-ui/src/components/dialog/dialog-globals.scss'
import '@mondough/monzo-ui/src/tokens/themes.scss'
import 'what-input'

import '../scss/globals.scss'

import { AppContext, AppProps } from 'next/app'
import React from 'react'

import {
  NextPageAnalytics,
  useNextScreenTracking,
} from '@mondough/analytics-v2'
import { CopyProvider } from '@mondough/copy'
import { type CopyBundle, loadCopyServerSide } from '@mondough/copy/server'
import {
  LocaleProvider,
  type ResolvedLocale,
  configureLocales,
  locale as liblocale,
  useLocaleCode,
} from '@mondough/locale'
import { MonzoUiThemeProvider, Theme } from '@mondough/monzo-ui'
import { getEnv, initErrorHandling } from '@mondough/next-utils'
import { displayExternalSelfXSSWarning } from '@mondough/self-xss-warning'

import { ErrorBoundary, Layout } from '../components'
import { ConsentManagement } from '../components/ConsentManagement'
import { AuthProvider, ReferralProvider, TabProvider } from '../providers'
import {
  generateHashedFingerprint,
  ignoredErrors,
  initializeServerSideLogging,
  legacyCacheName,
  legacyTimeStoreCacheName,
} from '../utils'

configureLocales({
  isProduction: getEnv('environment') === 'production',
  localeCookieDomain: getEnv('localeCookieDomain') ?? '',
})

type InitialProps = {
  hashedFingerprint?: string | null
  copy?: CopyBundle
  locale?: ResolvedLocale
}
interface AppPropsWithAnalytics extends AppProps<InitialProps> {
  analytics: NextPageAnalytics
}

// Delete legacy cache stores which contained profile image data
// We shouldn't be caching these anymore as reported in #inc-customer-image-accessible-after-profile-privacy-disabled-2025-01-31
async function deleteOldCacheStores() {
  const cacheStoreNames = [legacyCacheName, legacyTimeStoreCacheName]
  if (!window.caches) {
    // Caching API not supported
    return
  }
  for (const cacheStoreName of cacheStoreNames) {
    await window.caches.delete(cacheStoreName)
  }
}

const LocaleAwareProviders = ({
  children,
  copy,
}: {
  children: React.ReactNode
  copy: CopyBundle | null
}) => {
  const localeCode = useLocaleCode()

  return (
    <CopyProvider appName="app.tabs-monzo-me" locale={localeCode} copy={copy}>
      <ConsentManagement>{children}</ConsentManagement>
    </CopyProvider>
  )
}

const App = (props: AppPropsWithAnalytics) => {
  const { Component, pageProps } = props

  const ranSetup = React.useRef(false)

  const copy = pageProps?.copy ? { ...pageProps.copy } : null
  const locale = pageProps?.locale ? { ...pageProps.locale } : null

  useNextScreenTracking(props)
  initializeServerSideLogging()

  React.useEffect(() => {
    const setup = async () => {
      displayExternalSelfXSSWarning()
      initErrorHandling(undefined, ignoredErrors, 'app.tabs-monzo-me')
      await deleteOldCacheStores()
    }

    if (!ranSetup.current) {
      void setup()
      ranSetup.current = true
    }
  }, [])

  return (
    <MonzoUiThemeProvider initialTheme={Theme.LightRefreshed}>
      <ErrorBoundary>
        <AuthProvider hashedFingerprint={pageProps.hashedFingerprint ?? null}>
          <LocaleProvider initialLocale={locale} updateHtmlLang={true}>
            <LocaleAwareProviders copy={copy}>
              <ReferralProvider>
                <TabProvider>
                  <Layout {...pageProps}>
                    <Component {...pageProps} />
                  </Layout>
                </TabProvider>
              </ReferralProvider>
            </LocaleAwareProviders>
          </LocaleProvider>
        </AuthProvider>
      </ErrorBoundary>
    </MonzoUiThemeProvider>
  )
}

App.getInitialProps = async ({ Component, ctx }: AppContext) => {
  let pageProps: InitialProps = {}
  if (Component.getInitialProps) {
    pageProps = await Component.getInitialProps(ctx)
  }
  if (ctx.req) {
    try {
      pageProps = {
        ...pageProps,
        hashedFingerprint: await generateHashedFingerprint(ctx.req),
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching hashed fingerprint', error)
    }
  }

  let copyBundle: CopyBundle | null = null
  let localeResolved: ResolvedLocale | null = null

  try {
    localeResolved = liblocale.fromRequestServerSide(ctx)
    copyBundle = await loadCopyServerSide('app.tabs-monzo-me', {
      apiHost: getEnv('apiPath'),
      clientId: getEnv('clientId'),
      clientSecret: getEnv('clientSecret'),
    })
  } catch (err) {
    const msg = err instanceof Error ? err.message : err
    console.warn('[lib.copy]: Could not fetch copy bundle.', msg)
  }

  return {
    pageProps: {
      ...pageProps,
      copy: copyBundle,
      locale: localeResolved,
    },
  }
}

export default App
